import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Bell } from "lucide-react";
import api, { customerHeaders, getCustomerKey } from "@/lib/api";

// Customers stay anonymous (no phone / email), so the site itself tells them when a shop sends an offer:
// a bell with a count in the header and a one-time pop-up message. Checked every 45 s and when the tab
// becomes visible again; only for visitors who have asked shops for offers on this device.
const SEEN = "vlx_seen_offer_ids";
const readSeen = () => { try { return new Set(JSON.parse(localStorage.getItem(SEEN) || "[]")); } catch { return new Set(); } };
const writeSeen = (ids) => { try { localStorage.setItem(SEEN, JSON.stringify([...ids].slice(-300))); } catch { /* private mode */ } };

export const OfferBell = ({ t }) => {
  const [count, setCount] = useState(0);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const toasted = useRef(new Set());

  useEffect(() => {
    if (!getCustomerKey()) return undefined;
    let stop = false;
    const check = async () => {
      if (document.visibilityState === "hidden") return;
      try {
        const { data } = await api.get("/my-offers", { headers: customerHeaders() });
        if (stop) return;
        const ids = (data.offers || []).map((o) => o.id);
        const seen = readSeen();
        if (pathname === "/my-offers") { ids.forEach((i) => seen.add(i)); writeSeen(seen); setCount(0); return; }
        const fresh = (data.offers || []).filter((o) => !seen.has(o.id));
        setCount(fresh.length);
        const newOnes = fresh.filter((o) => !toasted.current.has(o.id));
        if (newOnes.length) {
          newOnes.forEach((o) => toasted.current.add(o.id));
          toast.success(t("bell_new_offer", { n: newOnes.length }), {
            action: { label: t("bell_see"), onClick: () => navigate("/my-offers") }, duration: 8000,
          });
        }
      } catch { /* offline: try again later */ }
    };
    check();
    const id = setInterval(check, 45000);
    document.addEventListener("visibilitychange", check);
    return () => { stop = true; clearInterval(id); document.removeEventListener("visibilitychange", check); };
  }, [pathname, navigate, t]);

  if (!getCustomerKey()) return null;
  return (
    <button data-testid="offer-bell" onClick={() => navigate("/my-offers")} aria-label={t("mo_title")}
      className="relative flex h-10 w-10 items-center justify-center rounded-full text-brand-navy hover:bg-brand-light">
      <Bell className={`h-5 w-5 ${count ? "animate-pulse" : ""}`} />
      {count > 0 && (
        <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-gradient-to-r from-amber-400 to-orange-500 px-1 text-[11px] font-bold text-slate-900 ring-2 ring-white">
          {count}
        </span>
      )}
    </button>
  );
};
